<script>
  import { FormTheme, Loader } from "@gl/design-system";
  import { Navbar, Callout, NewVersionCallout } from "./components/index";
  import { Router, Route, navigate } from "svelte-routing";
  import {
    Login,
    Contracts,
    Home,
    Forbidden,
    TemporarilyUnavailable,
    Me,
  } from "./pages/index";
  import {
    user,
    callout,
    contract,
    language,
    navbarItems,
    theme,
  } from "./stores/index";
  import { onMount, setContext } from "svelte";
  import "@gl/design-system/css";
  import "@gl/eup-player/dist/latest/app.css";
  import "./app.css";
  import { useStack } from "@gl/svelte/usables";
  import { writable } from "svelte/store";
  import { addMessages, init } from "svelte-i18n";
  import en from "./i18n/en.json";
  import de from "./i18n/de.json";
  import EventManagerContext from "./EventManagerContext.svelte";
  import AccessManagerContext from "./AccessManagerContext.svelte";
  import CutManagerContext from "./CutManagerContext.svelte";
  import Root from "./pages/Root.svelte";
  import Page404 from "./pages/Page404.svelte";
  import { UsersRepository } from "./repositories/index";
  import { IDP } from "./services/index";
  import { isBefore } from "date-fns";
  import { checkVersions } from "./utils";

  let newVersion = false;

  addMessages("en", en);
  addMessages("de", de);
  init({ fallbackLocale: "en", initialLocale: $language });

  import getFile from "./services/xml";
  import ContentManagerContext from "./ContentManagerContext.svelte";
  const config = getFile("/config.json");
  setContext("config", config);
  setContext("FormTheme", FormTheme);

  const itemsStore = writable([
    "access-manager",
    "channel-manager",
    "cut-manager",
    "content-manager",
  ]);
  const navbarItemsStore = useStack("GLOBAL_FE_NAVBAR_ITEMS", itemsStore);
  $: if (navbarItemsStore) navbarItems.set($navbarItemsStore);

  const load = async () => {
    await checkVersions(
      [
        "/index.html",
        ...(import.meta.env.MODE === "LOCAL_EVENT_MANAGER"
          ? []
          : ["/event-manager-frontend/event-manager.es.js"]),
      ],
      () => (newVersion = true),
    );
    if ($user?.token) {
      const now = new Date();
      if (isBefore(new Date($user.token.exp), now)) {
        await IDP.getToken();
      }
    }
  };
</script>

<svelte:head>
  <title>{config.NAME}</title>
</svelte:head>

{#if newVersion}
  <NewVersionCallout />
{/if}

{#await load()}
  <Loader />
{:then}
  <div class="root" class:theme-dark={$theme === "dark"}>
    <Router>
      {#if $user && $contract}
        <Navbar />
      {/if}

      <Route component={Forbidden} path="/forbidden" />

      {#if $user && $contract && $user.apps}
        <div class="content-container">
          <Route path="/" component={Root} />
          <Route path="/contracts" component={Contracts} />

          <Route path="/contracts/:CON/" component={Home} />

          <Route component={Page404} path="/404" />
          <Route
            component={TemporarilyUnavailable}
            path="/temporarily-unavailable"
          />

          <Route component={Me} path="/me/*" />

          {#if $user.apps.some((app) => app.id === "access-manager")}
            <Route path="/access-manager/*">
              <AccessManagerContext />
            </Route>
          {/if}

          {#if $user.apps?.some((app) => app.id === "channel-manager")}
            <Route path="/channel-manager/*">
              <EventManagerContext />
            </Route>
          {/if}

          {#if $user.apps?.some((app) => app.id === "cut-manager")}
            <Route path="/cut-manager/*">
              <CutManagerContext />
            </Route>
          {/if}

          {#if $user.apps?.some((app) => app.id === "content-manager")}
            <Route path="/content-manager/contracts/:CON/*">
              <ContentManagerContext />
            </Route>
          {/if}
        </div>
      {:else if $user && $user.data}
        <Route path="/" component={Contracts} />
        <Route path="*" component={Page404} />
      {:else}
        <Route path="/" component={Login} />
        <Route path="*" component={Login} />
      {/if}
    </Router>

    <Callout />
  </div>
{/await}

<style>
  .root {
    display: grid;
    grid-template-rows: auto 1fr;
    height: 100%;
  }

  .content-container {
    height: 100%;
    max-width: 100vw;
    overflow: auto;
  }
</style>
